import React, { ReactElement, useMemo, useState } from "react";
import { Alert, Form } from "react-bootstrap";
import ReactDom from "react-dom";
import { RouteComponentProps } from "react-router";
import { DataTable } from "../ui/DataTable";
import { ProjectBudget } from "./ProjectBudget";
import { ProjectBudgetService } from "./ProjectBudgetService";

interface IParams {
	id?: string;
}

export const ProjectsBudgetTable = (
	props: RouteComponentProps<IParams>,
): ReactElement => {
	const [budgets, setBudgets] = useState<ProjectBudget[]>();
	const [hideZeroBudgets, setHideZeroBudgets] = useState<boolean>(true);
	const portal = document.getElementById("account-project-budgets-portal");
	let output;

	if (!portal) {
		throw Error(
			"#account-project-budgets-portal element could not be found",
		);
	}

	const accountService = new ProjectBudgetService();

	const columns = [
		{
			Header: "ID",
			accessor: "id",
			width: "10%",
		},
		{
			Header: "Name",
			accessor: "name",
			Cell: ({ row: { original } }: any) => (
				<a href={`/project-budget/${original.id}`}>{original.name}</a>
			),
			width: "70%",
		},
		{
			Header: "Budget",
			accessor: "amount",
			Cell: ({ cell: { value } }: any) => <>${value}</>,
			Filter: false,
			width: "10%",
		},
		{
			Header: "Amount Available",
			accessor: "amountAvailable",
			Cell: ({ cell: { value } }: any) => <>${value}</>,
			Filter: false,
			width: "10%",
		},
	];

	useMemo(() => {
		const fetchBudgets = async () => {
			const budgetData = await accountService.findBudgetsForAccount!(
				props.match.params.id || "",
			);
			setBudgets(budgetData);
		};

		fetchBudgets();
	}, []);

	const filteredBudgets = useMemo(() => {
		if (!budgets) return [];
		return hideZeroBudgets 
			? budgets.filter(budget => budget.amountAvailable > 0)
			: budgets;
	}, [budgets, hideZeroBudgets]);

	if (!budgets || !budgets.length) {
		output = (
			<Alert variant="info">
				No budgets have been added to this account.
			</Alert>
		);
	} else if (!filteredBudgets.length && hideZeroBudgets) {
		output = (
			<>
				<Form.Check
					type="checkbox"
					id="hide-zero-budgets"
					label="Hide budgets with no amount available"
					checked={hideZeroBudgets}
					onChange={(e) => setHideZeroBudgets(e.target.checked)}
					className="mb-3"
				/>
				<Alert variant="info">
					No budgets with available amounts. Uncheck the filter above to see all budgets.
				</Alert>
			</>
		);
	} else {
		output = (
			<>
				<Form.Check
					type="checkbox"
					id="hide-zero-budgets"
					label="Hide budgets with no amount available"
					checked={hideZeroBudgets}
					onChange={(e) => setHideZeroBudgets(e.target.checked)}
					className="mb-3"
				/>
				<DataTable columns={columns} data={filteredBudgets} />
			</>
		);
	}

	return ReactDom.createPortal(output, portal);
};
